import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Bluetooth, Camera, Loader2, MessageCircleQuestion, MoreHorizontal, Terminal, Unlink } from 'lucide-react';
import { apiGet, apiPatch, apiPost } from '@/lib/api';
import { askCompanion, useAssistantStatus } from '@/lib/assistant';
import { useAuth } from '@/lib/auth';
import { fmtTime } from '@/lib/format';
import { order } from '@/lib/utils';
import { devicePatchPayload, deviceSiteReady } from '@/lib/device-edit-payload';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CommandPanel } from '@/components/CommandPanel';
import { DeviceRemoval } from '@/components/DeviceRemoval';
import { Input } from '@/components/ui/input';
import { Drawer } from '@/components/Drawer';
import { LivePanel } from '@/components/LivePanel';
import { PendingDevices } from '@/components/PendingDevices';
import { SubscribeBell } from '@/components/notifications/SubscribeBell';
import type { Device, Site } from '@/types';

/* 裝置詳情（GET /devices/{key}）回傳的形狀 */
interface Assignment {
  assignment_version: number;
  site_code: string;
  valid_from: string;
  valid_to?: string | null;
}
interface DeviceCommand {
  command_id: string;
  cmd_type: string;
  state: string;
  created_at: string;
  expires_at?: string | null;
  result?: Record<string, unknown> | null;
}
interface DeviceDetail extends Device {
  model?: string | null;
  assignments: Assignment[];
  commands: DeviceCommand[];
}

/** 命令狀態的中文與配色，對齊原生版 CMD_LABEL / CMD_CLS。 */
const CMD_LABEL: Record<string, string> = {
  created: '已建立', sent: '已下發', succeeded: '成功',
  failed: '失敗', timed_out: '逾時', running: '執行中',
};
const CMD_VARIANT: Record<string, 'success' | 'warn' | 'danger' | 'muted' | 'info'> = {
  created: 'muted', sent: 'info', succeeded: 'success',
  failed: 'danger', timed_out: 'warn', running: 'info',
};

export default function DevicesPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') ?? '';

  // 即時觀看：同時只開一路，關閉抽屜就釋放租約
  const [liveDevice, setLiveDevice] = useState<string | null>(null);
  const [detailKey, setDetailKey] = useState<string | null>(null);
  const [cmdDevice, setCmdDevice] = useState<Device | null>(null);
  const [editDevice, setEditDevice] = useState<Device | null>(null);
  const [removeKey, setRemoveKey] = useState<string | null>(null);
  const canAsk = useAssistantStatus().data?.ready === true;

  /* 先關掉詳情抽屜，等焦點歸位後再打開小哨，兩個對話框才不會搶焦點 */
  function askAbout(deviceId: string) {
    setDetailKey(null);
    window.setTimeout(() => askCompanion({
      text: `裝置 ${deviceId} 現在的狀態如何？有沒有需要注意的地方？`,
      context: { path: '/devices', device_id: deviceId },
      fresh: true,
    }), 0);
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['devices'],
    queryFn: () => apiGet<Device[]>('/api/v1/devices'),
    refetchInterval: 10_000,   // 與原生版的輪詢節奏一致
  });

  // 待認領與已拒絕的裝置由 PendingDevices 區塊負責，主清單只列已認領的，
  // 否則同一台會在畫面上出現兩次。claim_state 未定義視為已認領（舊資料）。
  const claimed = (data ?? []).filter((d) => (d.claim_state ?? 'claimed') === 'claimed');
  const rows = claimed.filter((d) => statusFilter !== 'offline' || !d.online);
  const canOperate = user?.role !== 'viewer';

  return (
    <>
    {/* 只有管理員能認領；沒有待認領裝置時元件自己回 null，不佔版面 */}
    {user?.role === 'admin' && (
      <div className="reveal mb-5" style={order(0)}><PendingDevices /></div>
    )}

    <div className="reveal mb-3 flex gap-2" style={order(1)} role="group" aria-label="裝置狀態篩選">
      {[['', '全部裝置'], ['offline', '僅顯示離線']].map(([value, label]) => (
        <Button key={value} size="sm" variant={statusFilter === value ? 'default' : 'secondary'}
          aria-pressed={statusFilter === value}
          onClick={() => { const next = new URLSearchParams(searchParams); value ? next.set('status', value) : next.delete('status'); setSearchParams(next); }}>
          {label}
        </Button>
      ))}
    </div>
    <Card className="reveal devices-list overflow-hidden" style={order(2)}>
      <CardHeader className="flex-wrap gap-3 border-b border-border pb-4 max-sm:items-stretch">
        <div>
          <CardTitle>裝置清單（{rows.length}）</CardTitle>
          <CardDescription>管理節點名稱、場域與即時串流能力</CardDescription>
        </div>

        {/* 新裝置配網入口。獨立頁面另開分頁：Web Bluetooth 需要使用者
            手勢直接觸發，包進 SPA 路由容易失效。 */}
        {user?.role === 'admin' && (
          <Button asChild variant="secondary" className="max-sm:w-full">
            <a href="/ble_prov.html" target="_blank" rel="noopener"
               title="用藍牙設定新裝置的 Wi-Fi、MQTT 與伺服器位址（限 Chrome / Edge）">
              <Bluetooth className="h-[15px] w-[15px]" />
              <span>藍牙配網</span>
            </a>
          </Button>
        )}
      </CardHeader>

      {isLoading ? (
        <div className="grid place-items-center py-20">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <p className="px-6 py-16 text-center text-[13px] text-destructive">
          載入失敗：{error instanceof Error ? error.message : '未知錯誤'}
        </p>
      ) : rows.length === 0 ? (
        <p className="px-6 py-16 text-center text-[13px] text-muted-foreground">
          尚無裝置。新裝置請用右上角的「藍牙配網」完成設定。
        </p>
      ) : (
        <div className="overflow-x-auto max-[720px]:overflow-visible">
          <table className="data-table w-full table-fixed border-collapse max-[960px]:table-auto">
            <thead>
              <tr>
                <th className="w-[18%]">裝置</th>
                <th>場域</th>
                <th>狀態</th>
                <th className="max-[720px]:hidden">韌體</th>
                <th className="!text-right max-[720px]:hidden">事件數</th>
                <th>最後心跳</th>
                <th className="w-[300px]" />
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.device_id}>
                  <td data-label="裝置">
                    <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                    {d.label ? (
                      <>
                        <span className="block font-semibold">{d.label}</span>
                        <span className="block truncate font-mono text-[11px] text-muted-foreground" title={d.device_id}>
                          {d.device_id}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="block truncate font-mono font-semibold" title={d.device_id}>{d.device_id}</span>
                        <Badge variant="warn" className="mt-1">待命名</Badge>
                      </>
                    )}
                    </div>
                    {/* 像訂閱頻道一樣，單獨調整這台裝置要通知到什麼程度 */}
                    <SubscribeBell scope="device" scopeKey={d.device_id} name={d.label || d.device_id} siteCode={d.site_code} />
                    </div>
                  </td>
                  <td data-label="場域">
                    {d.site_code ? (
                      <>
                        <span className="font-mono text-[11.5px]">{d.site_code}</span>
                        <span className="mx-1.5 text-border">｜</span>
                        <span>{d.site_name}</span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">未指派</span>
                    )}
                  </td>
                  <td data-label="狀態">
                    <Badge variant={d.online ? 'success' : 'muted'}>
                      {/* 線上的點會脈動，一眼看得出哪些裝置活著 */}
                      <span className={`h-1.5 w-1.5 rounded-full ${
                        d.online ? 'status-pulse bg-success' : 'bg-muted-foreground'
                      }`} />
                      {d.online ? '線上' : '離線'}
                    </Badge>
                  </td>
                  <td data-label="韌體" className="font-mono text-[11.5px] max-[720px]:hidden">
                    {d.firmware_version ?? '—'}
                  </td>
                  <td data-label="事件數" className="tabular text-right max-[720px]:hidden">{d.event_count ?? 0}</td>
                  <td data-label="最後心跳"
                      className="font-mono text-[11.5px] text-muted-foreground">
                    {fmtTime(d.last_seen_at)}
                  </td>
                  <td data-label="操作">
                    <div className="flex flex-wrap justify-end gap-1.5 max-[720px]:grid max-[720px]:grid-cols-[1fr_44px]">
                      <Button size="sm" disabled={!d.online} variant={d.online ? 'default' : 'secondary'}
                              className="max-[720px]:min-w-0"
                              title={d.online ? '開啟即時觀看' : '裝置離線，無法即時觀看'}
                              onClick={() => setLiveDevice(d.device_id)}>
                        <Camera className="h-[14px] w-[14px]" />
                        <span>即時觀看</span>
                      </Button>
                      {canOperate && (
                        <>
                          <Button size="sm" variant="secondary" className="max-[720px]:hidden"
                                  onClick={() => setEditDevice(d)}>設定</Button>
                        </>
                      )}
                      <Button size="sm" variant="secondary"
                              className={canOperate ? 'max-[720px]:hidden' : undefined}
                              onClick={() => setDetailKey(d.device_id)}>詳情</Button>
                      {canOperate && (
                        <DropdownMenu.Root>
                          <DropdownMenu.Trigger asChild>
                            <Button size="sm" variant="ghost" className="max-[720px]:px-0"
                                    aria-label={`開啟 ${d.label || d.device_id} 的更多操作`}>
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="max-[720px]:sr-only">更多</span>
                            </Button>
                          </DropdownMenu.Trigger>
                          <DropdownMenu.Portal>
                            <DropdownMenu.Content
                              align="end"
                              sideOffset={6}
                              className="z-50 min-w-[168px] rounded-md border border-border bg-card p-1 shadow-lg"
                            >
                              <DropdownMenu.Item
                                className="hidden min-h-[44px] cursor-pointer select-none items-center gap-2 rounded-sm px-3 text-[13px] font-medium outline-none hover:bg-secondary focus:bg-secondary max-[720px]:flex"
                                onSelect={() => setEditDevice(d)}
                              >
                                設定
                              </DropdownMenu.Item>
                              <DropdownMenu.Item
                                className="hidden min-h-[44px] cursor-pointer select-none items-center gap-2 rounded-sm px-3 text-[13px] font-medium outline-none hover:bg-secondary focus:bg-secondary max-[720px]:flex"
                                onSelect={() => setDetailKey(d.device_id)}
                              >
                                詳情
                              </DropdownMenu.Item>
                              <DropdownMenu.Item
                                className="flex min-h-[44px] cursor-pointer select-none items-center gap-2 rounded-sm px-3 text-[13px] font-medium outline-none hover:bg-secondary focus:bg-secondary"
                                onSelect={() => setCmdDevice(d)}
                              >
                                <Terminal className="h-4 w-4 text-muted-foreground" />
                                下命令
                              </DropdownMenu.Item>
                              {/* 解除配對會停掉裝置的資料接收，限管理員；後端端點也要求 admin */}
                              {user?.role === 'admin' && (
                                <>
                                  <DropdownMenu.Separator className="my-1 h-px bg-border" />
                                  <DropdownMenu.Item
                                    className="flex min-h-[44px] cursor-pointer select-none items-center gap-2 rounded-sm px-3 text-[13px] font-medium text-destructive outline-none hover:bg-accent focus:bg-accent"
                                    onSelect={() => setRemoveKey(d.device_id)}
                                  >
                                    <Unlink className="h-4 w-4" />
                                    解除配對或刪除…
                                  </DropdownMenu.Item>
                                </>
                              )}
                            </DropdownMenu.Content>
                          </DropdownMenu.Portal>
                        </DropdownMenu.Root>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>

    {/* 即時觀看抽屜。關閉時 LivePanel 卸載，useLive 會釋放租約，
        後端收割後通知裝置停止推流。 */}
    <Drawer
      open={liveDevice !== null}
      onOpenChange={(v) => !v && setLiveDevice(null)}
      title={`即時觀看 · ${liveDevice ?? ''}`}
      wide
    >
      {liveDevice && <LivePanel deviceKey={liveDevice} />}
    </Drawer>

    <Drawer
      open={detailKey !== null}
      onOpenChange={(v) => !v && setDetailKey(null)}
      title={`裝置 ${detailKey ?? ''}`}
      wide
    >
      {detailKey && <DeviceDetailBody deviceKey={detailKey} onAsk={canAsk ? () => askAbout(detailKey) : undefined} />}
    </Drawer>

    <Drawer
      open={cmdDevice !== null}
      onOpenChange={(v) => !v && setCmdDevice(null)}
      title={`對 ${cmdDevice?.device_id ?? ''} 下命令`}
    >
      {cmdDevice && <CommandPanel device={cmdDevice} />}
    </Drawer>

    <Drawer
      open={removeKey !== null}
      onOpenChange={(v) => !v && setRemoveKey(null)}
      title={`解除配對 ${removeKey ?? ''}`}
    >
      {removeKey && <DeviceRemoval deviceKey={removeKey} onDone={() => setRemoveKey(null)} />}
    </Drawer>

    <Drawer
      open={editDevice !== null}
      onOpenChange={(v) => !v && setEditDevice(null)}
      title={`設定 ${editDevice?.device_id ?? ''}`}
    >
      {editDevice && (
        <DeviceEditForm
          device={editDevice}
          onDone={() => {
            setEditDevice(null);
            void qc.invalidateQueries({ queryKey: ['devices'] });
          }}
        />
      )}
    </Drawer>
    </>
  );
}

/* ── 詳情 ─────────────────────────────────────────── */

function DeviceDetailBody({ deviceKey, onAsk }: { deviceKey: string; onAsk?: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['device', deviceKey],
    queryFn: () => apiGet<DeviceDetail>(`/api/v1/devices/${encodeURIComponent(deviceKey)}`),
  });

  if (isLoading) {
    return <div className="grid place-items-center py-16">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
    </div>;
  }
  if (error || !data) {
    return <p className="text-[13px] text-destructive">
      載入失敗：{error instanceof Error ? error.message : '未知錯誤'}
    </p>;
  }

  return (
    <div className="space-y-7">
      <dl className="grid grid-cols-[96px_minmax(0,1fr)] gap-y-2.5 text-[13px]">
        <dt className="text-muted-foreground">裝置編號</dt>
        <dd className="font-mono">{data.device_id}</dd>
        <dt className="text-muted-foreground">韌體</dt>
        <dd className="font-mono">{data.firmware_version || '—'}</dd>
        <dt className="text-muted-foreground">型號</dt>
        <dd>{data.model || '—'}</dd>
        <dt className="text-muted-foreground">最後心跳</dt>
        <dd className="font-mono">{fmtTime(data.last_seen_at)}</dd>
        <dt className="self-center text-muted-foreground">通知</dt>
        <dd><SubscribeBell scope="device" scopeKey={data.device_id} name={data.label || data.device_id} siteCode={data.site_code} showLabel /></dd>
      </dl>

      {onAsk && <div className="flex flex-wrap items-center gap-3 rounded-md border border-border px-3.5 py-3">
        <p className="min-w-0 flex-1 text-[12.5px] leading-relaxed text-muted-foreground">
          想知道這台最近的狀況、異常或上傳情形，可以直接問小哨。
        </p>
        <Button size="sm" variant="secondary" onClick={onAsk}>
          <MessageCircleQuestion className="h-4 w-4" />問小哨
        </Button>
      </div>}

      <section>
        <h3 className="mb-2.5 font-mono text-[11px] font-bold uppercase tracking-wider
                       text-muted-foreground">場域指派歷程</h3>
        {data.assignments?.length ? (
          <div className="space-y-1.5">
            {data.assignments.map((a) => (
              <div key={a.assignment_version}
                   className="flex flex-wrap items-center gap-2.5 text-[12.5px]">
                <Badge variant="muted">v{a.assignment_version}</Badge>
                <span className="font-mono">{a.site_code}</span>
                <span className="text-muted-foreground">
                  {fmtTime(a.valid_from)} → {a.valid_to ? fmtTime(a.valid_to) : '目前'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[12.5px] text-muted-foreground">尚無紀錄</p>
        )}
      </section>

      <section>
        <h3 className="mb-2.5 font-mono text-[11px] font-bold uppercase tracking-wider
                       text-muted-foreground">近期命令</h3>
        {data.commands?.length ? (
          <div className="space-y-1.5">
            {data.commands.map((c, i) => (
              <div key={`${c.cmd_type}-${c.created_at}-${i}`}
                   className="flex flex-wrap items-center gap-2.5 text-[12.5px]">
                <span className="font-mono">{c.cmd_type}</span>
                <Badge variant={CMD_VARIANT[c.state] ?? 'muted'}>
                  {CMD_LABEL[c.state] ?? c.state}
                </Badge>
                <span className="text-muted-foreground">{fmtTime(c.created_at)}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[12.5px] text-muted-foreground">尚無命令紀錄</p>
        )}
      </section>
    </div>
  );
}

/* ── 設定 ─────────────────────────────────────────── */

const KINDS: [string, string][] = [
  ['camera_audio', '影像＋聲音'],
  ['camera', '僅影像'],
  ['audio', '僅聲音'],
];

/** 隨機產生一組高熵金鑰（32 bytes → 64 碼 hex），不必請管理員自己想。 */
function randomSecret(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function DeviceEditForm({ device, onDone }: { device: Device; onDone: () => void }) {
  const { user } = useAuth();
  const [err, setErr] = useState('');
  const [secretMsg, setSecretMsg] = useState('');
  const [secretErr, setSecretErr] = useState('');
  const [siteCode, setSiteCode] = useState(device.site_code ?? '');
  const [siteChanged, setSiteChanged] = useState(false);

  const { data: sites, isPending: sitesPending, error: sitesError } = useQuery({
    queryKey: ['sites'],
    queryFn: () => apiGet<Site[]>('/api/v1/sites'),
  });
  const { data: assignments } = useQuery({
    queryKey: ['device-assignments', device.device_id],
    queryFn: () => apiGet<Assignment[]>(
      `/api/v1/devices/${encodeURIComponent(device.device_id)}/assignments`),
  });
  const sitesReady = deviceSiteReady(device.site_code, sites, sitesPending, !!sitesError);

  const mut = useMutation({
    mutationFn: (v: { label: string; kind: string; site_code?: string }) =>
      apiPatch(`/api/v1/devices/${encodeURIComponent(device.device_id)}`, v),
    onSuccess: onDone,
    onError: (e) => setErr(e instanceof Error ? e.message : '儲存失敗'),
  });

  /* 重設裝置密鑰：後端只記 pending，經 /config topic 下發給裝置，
     裝置回報 config-report 確認版本後才真正生效——這裡不需要、也不該
     顯示明文金鑰讓管理員手動輸入到裝置上。 */
  const secretMut = useMutation({
    mutationFn: (secret: string) =>
      apiPost<{ pending: boolean; secret_pending_version: number; message: string }>(
        `/api/v1/devices/${encodeURIComponent(device.device_id)}/secret`, { secret }),
    onSuccess: (r) => { setSecretErr(''); setSecretMsg(r.message || '已排入下發'); },
    onError: (e) => { setSecretMsg(''); setSecretErr(e instanceof Error ? e.message : '重設失敗'); },
  });

  function onResetSecret() {
    setSecretMsg('');
    setSecretErr('');
    secretMut.mutate(randomSecret());
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr('');
    if (!sitesReady) return;
    const fd = new FormData(e.currentTarget);
    mut.mutate(devicePatchPayload(String(fd.get('label') ?? ''), String(fd.get('kind') ?? 'camera_audio'), device.site_code, siteCode, siteChanged));
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <label className="block">
        <span className="mb-2 block text-[12.5px] font-semibold">顯示名稱</span>
        <Input name="label" defaultValue={device.label ?? ''} maxLength={80}
               placeholder={device.device_id} />
        <span className="mt-1.5 block text-[11.5px] text-muted-foreground">
          裝置代號 {device.device_id} 不會變，改的只是顯示名稱
        </span>
      </label>

      <label className="block">
        <span className="mb-2 block text-[12.5px] font-semibold">裝置種類</span>
        <select name="kind" defaultValue={device.kind ?? 'camera_audio'}
                className="min-h-[44px] w-full rounded-sm border border-border bg-card px-3 text-sm">
          {KINDS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
        </select>
      </label>

      <label className="block">
        <span className="mb-2 block text-[12.5px] font-semibold">所屬場域</span>
        <select name="site_code" value={siteCode}
                disabled={sitesPending || !!sitesError}
                onChange={(e) => { setSiteCode(e.target.value); setSiteChanged(true); }}
                className="min-h-[44px] w-full rounded-sm border border-border bg-card px-3 text-sm">
          <option value="" disabled>{sitesPending ? '載入場域中…' : '尚未指派場域'}</option>
          {siteCode && !sites?.some((s) => s.code === siteCode) && (
            <option value={siteCode}>{siteCode}（目前場域）</option>
          )}
          {(sites ?? []).map((s) => (
            <option key={s.code} value={s.code}>{s.code}　{s.name}</option>
          ))}
        </select>
        <span className="mt-1.5 block text-[11.5px] text-muted-foreground">
          改場域只影響之後的事件；既有事件仍歸屬當時的場域
        </span>
        {sitesError && <span className="mt-1.5 block text-[11.5px] text-destructive">場域載入失敗，請稍後重試。</span>}
      </label>

      {err && <p className="text-[12.5px] font-semibold text-destructive">{err}</p>}

      <Button type="submit" disabled={mut.isPending || !sitesReady}>
        {mut.isPending ? '儲存中…' : '儲存'}
      </Button>

      <section className="border-t border-border pt-5">
        <h3 className="mb-2.5 font-mono text-[11px] font-bold uppercase tracking-wider
                       text-muted-foreground">場域指派紀錄</h3>
        {assignments?.length ? (
          <div className="space-y-1.5">
            {assignments.map((a) => (
              <div key={a.assignment_version}
                   className="flex flex-wrap items-center gap-2.5 text-[12.5px]">
                <Badge variant="muted">v{a.assignment_version}</Badge>
                <span className="font-mono">{a.site_code}</span>
                <span className="text-muted-foreground">
                  {fmtTime(a.valid_from)} → {a.valid_to ? fmtTime(a.valid_to) : '目前'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[12.5px] text-muted-foreground">尚無紀錄</p>
        )}
      </section>

      {/* 密鑰重設僅限 admin——後端 /secret 端點本身也要求 admin，
          這裡先擋一層避免非 admin 點了才被 403。 */}
      {user?.role === 'admin' && (
        <section className="border-t border-border pt-5">
          <h3 className="mb-2.5 font-mono text-[11px] font-bold uppercase tracking-wider
                         text-muted-foreground">裝置密鑰</h3>
          <p className="mb-3 text-[12.5px] text-muted-foreground">
            裝置 NVS 裡的密鑰與平台不一致時，上傳會一直收到 401。按下重設會產生一組新密鑰、經 MQTT /config 主題下發給裝置，裝置回報套用成功後平台才會生效——不需要手動把密鑰輸入到裝置上。
          </p>
          <Button type="button" variant="secondary" disabled={secretMut.isPending}
                  onClick={onResetSecret}>
            {secretMut.isPending ? '下發中…' : '重設金鑰（自動下發給裝置）'}
          </Button>
          {secretMsg && (
            <p className="mt-2 text-[12.5px] font-semibold text-success-on">{secretMsg}</p>
          )}
          {secretErr && (
            <p className="mt-2 text-[12.5px] font-semibold text-destructive">{secretErr}</p>
          )}
        </section>
      )}
    </form>
  );
}
